import { Injectable } from '@angular/core';
import { Selector, createSelector } from '@ngxs/store';
import { PageAction } from '@progress/kendo-angular-grid/dist/es2015/scrolling/scroller.service';
import _, { concat } from 'lodash';
import { GreetingIciTypeEnum } from 'src/app/components/settings/global-settings/greeting-settings/greeting-ici-type-enum';
import { IciHelperSelector } from 'src/app/store/samwin/ici-helper.selector';
import { SamwinSkillsState } from 'src/app/store/samwin/samwin-skills/samwin-skills.state';
import { SkillGroup } from 'src/app/store/samwin/samwin-skills/skill-group';
import { IciFlags } from 'src/generated/signalR/ici-flags';
import { AgentGreetingConfig, AgentGreetingItem, AgentGreetingSettingsState, AudioFileInfo } from './agent-greeting-settings.state';

@Injectable()
export class AgentGreetingSettingsSelector {
   @Selector([AgentGreetingSettingsState.isIciDisplayGrouped])
   public static isIciExpanded(expanded: boolean) {
      return !expanded;
   }

   @Selector([SamwinSkillsState.callSkillsInclusiveOverallSkill])
   public static greetingRelatedSkills(skills: SkillGroup[]) {
      return skills;
   }

   @Selector([AgentGreetingSettingsSelector.greetingRelatedSkills, AgentGreetingSettingsState.currentPage])
   public static pagedGreetingRelatedSkills(skills: SkillGroup[], page: PageAction) {
      if (_.isNil(page)) {
         return skills;
      }
      return skills.slice(page.skip, page.skip + page.take);
   }

   @Selector([
      AgentGreetingSettingsSelector.greetingRelatedSkills,
      AgentGreetingSettingsState.getWavesForSkill,
      AgentGreetingSettingsState.separatedFilterValues,
      AgentGreetingSettingsSelector.getIsFilterValueMatching,
   ])
   public static greetingSkills(
      skills: SkillGroup[],
      getWavesForSkill: (skillId: number) => AudioFileInfo[],
      skillFilterValues: string[],
      isFilterValueMatching: (displayValue: string, filterValues: string[]) => boolean
   ) {
      if (_.isNil(skillFilterValues) || _.isEmpty(skillFilterValues)) {
         return skills;
      }
      return _(skills)
         .filter(
            (skill) =>
               isFilterValueMatching(skill.displayName, skillFilterValues) ||
               getWavesForSkill(skill.skillId).some((x) => isFilterValueMatching(x.name, skillFilterValues))
         )
         .value();
   }

   @Selector()
   private static getIsFilterValueMatching() {
      return (displayValue: string, filterValues: string[]) => {
         return _(filterValues)
            .map((filterValue) => displayValue.toLowerCase().includes(filterValue.toLowerCase()))
            .every();
      };
   }

   @Selector()
   private static getDropZoneName() {
      return (skillId: number, ici: GreetingIciTypeEnum) => {
         return skillId + '-' + ici?.name;
      };
   }

   public static getDropZone(skillId: number, ici: GreetingIciTypeEnum) {
      return createSelector([AgentGreetingSettingsSelector.getDropZoneName], (getDropZoneName: (skillId: number, ici: GreetingIciTypeEnum) => string) => {
         return getDropZoneName(skillId, ici);
      });
   }

   public static getIciEnumType(ici: number) {
      return createSelector([AgentGreetingSettingsSelector.getIciTypeById], (getIciTypeById: (ici: number) => GreetingIciTypeEnum) => {
         return getIciTypeById(ici);
      });
   }

   @Selector([
      AgentGreetingSettingsSelector.getIsDropZoneNotAvailable,
      AgentGreetingSettingsSelector.getDropZoneName,
      AgentGreetingSettingsSelector.getIsConfigConflicting,
   ])
   public static getAvailableDropZoneName(
      IsDropZoneNotAvailable: (skillId: number, ici: number) => boolean,
      getDropZoneName: (skillId: number, ici: GreetingIciTypeEnum) => string,
      getIsConfigConflicting: (skillId: number, ici: number) => boolean
   ) {
      return (skillId: number, ici: GreetingIciTypeEnum) => {
         if (IsDropZoneNotAvailable(skillId, ici.value) || getIsConfigConflicting(skillId, ici.value)) {
            return null;
         }
         return getDropZoneName(skillId, ici);
      };
   }

   @Selector([AgentGreetingSettingsState.getIsWaveAvailableForSkillAndIci])
   public static getIsDropZoneNotAvailable(getIsWaveAvailableForSkillAndIci: (skillId: number, ici: number) => boolean) {
      return (skillId: number, ici: number) => {
         return getIsWaveAvailableForSkillAndIci(skillId, ici);
      };
   }

   @Selector([
      AgentGreetingSettingsState.wasValuePersisted,
      AgentGreetingSettingsSelector.pagedGreetingRelatedSkills,
      AgentGreetingSettingsSelector.getAvailableDropZoneName,
      AgentGreetingSettingsSelector.currentIciFields,
   ])
   public static availableDropZones(
      wasValuePersisted: boolean,
      skills: SkillGroup[],
      getDropZoneName: (skillId: number, ici: GreetingIciTypeEnum) => string,
      availableIciFields: GreetingIciTypeEnum[]
   ) {
      if (!wasValuePersisted) {
         return [];
      }
      return _.flatMap(skills.map((x) => _.flatMap(availableIciFields.map((iciType) => [getDropZoneName(x.skillId, iciType)])))).filter((x) => !_.isNil(x));
   }

   @Selector([
      AgentGreetingSettingsSelector.availableIciFields,
      AgentGreetingSettingsSelector.getIsIciAvailable,
      AgentGreetingSettingsState.isIciDisplayGrouped,
   ])
   public static currentIciFields(availableIciFields: GreetingIciTypeEnum[], isIciAvailable: (type: GreetingIciTypeEnum) => boolean, isGrouped: boolean) {
      if (isGrouped) {
         return availableIciFields.filter((ici) => isIciAvailable(ici));
      }
      return availableIciFields;
   }

   @Selector([AgentGreetingSettingsSelector.currentIciFields])
   public static availableIciFieldNames(IciTypes: GreetingIciTypeEnum[]) {
      return IciTypes.map((ici) => ici?.enumName);
   }

   @Selector([AgentGreetingSettingsSelector.currentIciFields])
   public static displayedIciIds(IciTypes: GreetingIciTypeEnum[]) {
      return IciTypes.map((ici) => ici?.value);
   }

   @Selector([AgentGreetingSettingsSelector.baseIciFields])
   public static getIsIciAvailable(defaultIciFields: GreetingIciTypeEnum[]) {
      return (type: GreetingIciTypeEnum) => {
         return defaultIciFields.includes(type);
      };
   }

   @Selector([
      AgentGreetingSettingsState.isIciDisplayGrouped,
      AgentGreetingSettingsState.agentGreetingByWave,
      AgentGreetingSettingsSelector.getIciGroupTypeById,
   ])
   public static getGreetingForCurrentGroup(
      isIciDisplayGrouped: boolean,
      agentGreetingByWave: (waveId: number) => AgentGreetingItem[],
      getIciGroupTypeById: (ici: number) => GreetingIciTypeEnum
   ) {
      return (waveId: number) => {
         const wave = agentGreetingByWave(waveId);
         if (!isIciDisplayGrouped) {
            return wave;
         }
         return _(wave)
            .uniqBy((x) => `${x.key.queueId}-${getIciGroupTypeById(x.key.iciId)}`)
            .value();
      };
   }

   @Selector([AgentGreetingSettingsSelector.getGreetingForCurrentGroup, SamwinSkillsState.getSkillGroup, AgentGreetingSettingsSelector.getIciTypeById])
   public static getWaveAssignedGreetings(
      getGreetingForCurrentGroup: (waveId: number) => AgentGreetingItem[],
      getSkillConfiguration: (skillId: number) => SkillGroup,
      getIciTypeById: (id: number) => GreetingIciTypeEnum
   ) {
      return (waveId: number) => {
         const items: AgentGreetingItem[] = getGreetingForCurrentGroup(waveId);
         return items.map((x) => {
            return {
               skill: getSkillConfiguration(x.key.queueId),
               iciName: getIciTypeById(x.key.iciId)?.translation ?? '',
            };
         });
      };
   }

   @Selector([AgentGreetingSettingsSelector.getICIGroupEntries, AgentGreetingSettingsState.getSkillAgentGreetingConfig])
   public static getAssignedWaves(
      getICIGroupEntries: (baseIci: number) => GreetingIciTypeEnum[],
      getSkillAgentGreetingConfig: (skillId: number) => AgentGreetingConfig[]
   ) {
      return (skillId: number, baseIci) => {
         const config = getSkillAgentGreetingConfig(skillId);
         const iciGroup = getICIGroupEntries(baseIci);
         const items = config.filter((entry) => iciGroup.map((ici) => ici.value).includes(entry.assignment.key.iciId));

         return iciGroup.map((ici) => {
            return {
               iciName: ici.translation ?? '',
               wave: items.find((item) => ici.value === item.assignment.key.iciId)?.wave,
            };
         });
      };
   }

   @Selector([AgentGreetingSettingsSelector.getWaveAssignedGreetings])
   public static getIsWaveAssigned(
      getWaveAssignedGreetings: (
         waveId: number
      ) => {
         skill: SkillGroup;
         iciName: string;
      }[]
   ) {
      return (waveId: number) => {
         return !_.isEmpty(getWaveAssignedGreetings(waveId));
      };
   }

   @Selector([IciHelperSelector.getIsFallBack, AgentGreetingSettingsSelector.getIsBaseIci])
   public static getGreetingGroup(getIsFallBack: (iciId: number) => boolean, isBaseIci: (iciId: number) => boolean) {
      return (agentGreeting: AgentGreetingItem) => {
         const entries: AgentGreetingItem[] = [];
         entries.push(agentGreeting);
         if (agentGreeting.key.iciId === 0 || !isBaseIci(agentGreeting.key.iciId)) {
            return entries;
         }

         if (!getIsFallBack(agentGreeting.key.iciId)) {
            entries.push({
               key: {
                  iciId: agentGreeting.key.iciId + IciFlags.TRANSFERRED,
                  queueId: agentGreeting.key.queueId,
               },
               waveId: agentGreeting.waveId,
            });
         }
         entries.push({
            key: {
               iciId: agentGreeting.key.iciId + IciFlags.BUSY,
               queueId: agentGreeting.key.queueId,
            },
            waveId: agentGreeting.waveId,
         });
         entries.push({
            key: {
               iciId: agentGreeting.key.iciId + IciFlags.FORWARDED,
               queueId: agentGreeting.key.queueId,
            },
            waveId: agentGreeting.waveId,
         });
         entries.push({
            key: {
               iciId: agentGreeting.key.iciId + IciFlags.NO_ANSWER,
               queueId: agentGreeting.key.queueId,
            },
            waveId: agentGreeting.waveId,
         });

         return entries;
      };
   }

   @Selector([AgentGreetingSettingsSelector.baseIciFields, AgentGreetingSettingsSelector.getIciTypeById])
   private static getIsBaseIci(baseIciFields: GreetingIciTypeEnum[], getIciTypeById: (ici: number) => GreetingIciTypeEnum) {
      return (iciId: number) => {
         return baseIciFields.includes(getIciTypeById(iciId));
      };
   }

   @Selector([AgentGreetingSettingsSelector.baseIciFields, AgentGreetingSettingsSelector.subIciFields])
   public static availableIciFields(baseIciFields: GreetingIciTypeEnum[], subIciFields: GreetingIciTypeEnum[]) {
      return _(concat(baseIciFields, subIciFields))
         .sortBy((x) => x.displayOrder)
         .value();
   }

   @Selector([AgentGreetingSettingsSelector.availableIciFields])
   public static availableIciIds(IciTypes: GreetingIciTypeEnum[]) {
      return IciTypes.map((ici) => ici?.value);
   }

   @Selector([AgentGreetingSettingsState.getSkillAgentGreetingConfig, AgentGreetingSettingsSelector.getIciGroupTypeById])
   public static getGroupConfig(
      getSkillAgentGreetingConfig: (skillId: number) => AgentGreetingConfig[],
      getIciGroupTypeById: (ici: number) => GreetingIciTypeEnum
   ) {
      return (skillId: number, baseIci: number) => {
         return getSkillAgentGreetingConfig(skillId).filter((x) => getIciGroupTypeById(x.assignment.key.iciId).value === baseIci);
      };
   }

   @Selector([AgentGreetingSettingsSelector.getGroupConfig])
   public static getWavesForGroup(getGroupConfig: (skillId: number, baseIci: number) => AgentGreetingConfig[]) {
      return (skillId: number, baseIci: number) => {
         return getGroupConfig(skillId, baseIci).map((gr) => gr.wave);
      };
   }

   @Selector([AgentGreetingSettingsSelector.getWavesForGroup])
   public static getUniqueWaveIds(agentGreetingItems: (skillId: number, baseIci: number) => AudioFileInfo[]) {
      return (skillId: number, baseIci: number) => {
         return _(agentGreetingItems(skillId, baseIci).map((wave) => wave?.id))
            .uniq()
            .value();
      };
   }

   @Selector([AgentGreetingSettingsSelector.availableIciIds, AgentGreetingSettingsSelector.getIciGroupTypeById])
   public static getICIGroupIds(availableIciIds: number[], getIciGroupTypeById: (ici: number) => GreetingIciTypeEnum) {
      return (baseIci: number) => {
         return availableIciIds.filter((iciId) => getIciGroupTypeById(iciId).value === baseIci);
      };
   }

   @Selector([AgentGreetingSettingsSelector.getICIGroupIds, AgentGreetingSettingsSelector.getIciTypeById])
   public static getICIGroupEntries(availableIciIds: (baseIci: number) => number[], getIciTypeById: (ici: number) => GreetingIciTypeEnum) {
      return (baseIci: number) => {
         return availableIciIds(baseIci).map((iciId) => getIciTypeById(iciId));
      };
   }

   @Selector([AgentGreetingSettingsSelector.getICIGroupIds])
   public static getICIGroupCount(getICIGroup: (ici: number) => number[]) {
      return (baseIci: number) => {
         return getICIGroup(baseIci).length;
      };
   }

   @Selector([AgentGreetingSettingsSelector.getWavesForGroup])
   public static getWaveGroupCount(getWavesForGroup: (skillId: number, baseIci: number) => number[]) {
      return (skillId: number, baseIci: number) => {
         return getWavesForGroup(skillId, baseIci).length;
      };
   }

   @Selector([AgentGreetingSettingsSelector.getWaveGroupCount, AgentGreetingSettingsSelector.getICIGroupCount])
   public static getAreAllWavesSet(getWaveGroupCount: (skillId: number, baseIci: number) => number, getICIGroupCount: (baseIci: number) => number) {
      return (skillId: number, baseIci: number) => {
         return getWaveGroupCount(skillId, baseIci) === 0 || getWaveGroupCount(skillId, baseIci) === getICIGroupCount(baseIci);
      };
   }

   @Selector([AgentGreetingSettingsSelector.getUniqueWaveIds])
   private static getUniqueWaveCount(getUniqueWaveIds: (skillId: number, baseIci: number) => number[]) {
      return (skillId: number, baseIci: number) => {
         return getUniqueWaveIds(skillId, baseIci).length;
      };
   }

   @Selector([
      AgentGreetingSettingsState.isIciDisplayGrouped,
      AgentGreetingSettingsSelector.getUniqueWaveCount,
      AgentGreetingSettingsSelector.getAreAllWavesSet,
   ])
   private static getIsConfigConflicting(
      isIciDisplayGrouped: boolean,
      getUniqueWaveCount: (skillId: number, baseIci: number) => number,
      getAreAllWavesSet: (skillId: number, baseIci: number) => number
   ) {
      return (skillId: number, baseIci: number) => {
         if (!isIciDisplayGrouped) {
            return false;
         }
         return getUniqueWaveCount(skillId, baseIci) > 1 || !getAreAllWavesSet(skillId, baseIci);
      };
   }

   @Selector([AgentGreetingSettingsSelector.getUniqueWaveIds, AgentGreetingSettingsState.waveById])
   private static getGroupedWaves(getUniqueWaveIds: (skillId: number, baseIci: number) => number[], waveById: (waveId: number) => AudioFileInfo) {
      return (skillId: number, baseIci: number) => {
         return getUniqueWaveIds(skillId, baseIci).map((waveId) => waveById(waveId));
      };
   }

   public static getIciConfigIsConflicting(skillId: number, baseIci: number) {
      return createSelector([AgentGreetingSettingsSelector.getIsConfigConflicting], (getIsConfigConflicting: (skillId: number, baseIci: number) => boolean) => {
         return getIsConfigConflicting(skillId, baseIci);
      });
   }

   public static getConfiguredWaveCount(skillId: number, baseIci: number) {
      return createSelector([AgentGreetingSettingsSelector.getUniqueWaveCount], (getUniqueWaveCount: (skillId: number, baseIci: number) => number) => {
         return getUniqueWaveCount(skillId, baseIci);
      });
   }

   public static getAudioFilesForGroup(skillId: number, baseIci: number) {
      return createSelector(
         [AgentGreetingSettingsSelector.getAssignedWaves],
         (
            getAssignedWaves: (
               skillId: number,
               baseIci: number
            ) => {
               iciName: string;
               wave: AudioFileInfo;
            }[]
         ) => {
            return getAssignedWaves(skillId, baseIci);
         }
      );
   }

   public static getIsGroupHeader(baseIci: number) {
      return createSelector([AgentGreetingSettingsSelector.getIsBaseIci], (getIsBaseIci: (baseIci: number) => boolean) => {
         return getIsBaseIci(baseIci);
      });
   }

   @Selector()
   public static baseIciFields() {
      return [
         GreetingIciTypeEnum.ALL,
         GreetingIciTypeEnum.INBOUND_INTERNAL,
         GreetingIciTypeEnum.INBOUND_INTERNAL_FALLBACK,
         GreetingIciTypeEnum.INBOUND_EXTERNAL,
         GreetingIciTypeEnum.INBOUND_EXTERNAL_FALLBACK,
      ];
   }

   @Selector()
   public static subIciFields() {
      return [
         GreetingIciTypeEnum.INBOUND_INTERNAL_BUSY,
         GreetingIciTypeEnum.INBOUND_INTERNAL_NO_ANSWER,
         GreetingIciTypeEnum.INBOUND_INTERNAL_FORWARDED,
         GreetingIciTypeEnum.INBOUND_INTERNAL_REDIRECTED,
         GreetingIciTypeEnum.INBOUND_INTERNAL_FALLBACK_BUSY,
         GreetingIciTypeEnum.INBOUND_INTERNAL_FALLBACK_NO_ANSWER,
         GreetingIciTypeEnum.INBOUND_INTERNAL_FALLBACK_FORWARDED,
         GreetingIciTypeEnum.INBOUND_EXTERNAL_BUSY,
         GreetingIciTypeEnum.INBOUND_EXTERNAL_NO_ANSWER,
         GreetingIciTypeEnum.INBOUND_EXTERNAL_FORWARDED,
         GreetingIciTypeEnum.INBOUND_EXTERNAL_REDIRECTED,
         GreetingIciTypeEnum.INBOUND_EXTERNAL_FALLBACK_BUSY,
         GreetingIciTypeEnum.INBOUND_EXTERNAL_FALLBACK_NO_ANSWER,
         GreetingIciTypeEnum.INBOUND_EXTERNAL_FALLBACK_FORWARDED,
      ];
   }

   @Selector([
      IciHelperSelector.getIsInboundInternalFallbackIsForwarded,
      IciHelperSelector.getIsInboundInternalFallbackIsNoAnswer,
      IciHelperSelector.getIsInboundInternalFallbackBusy,
      IciHelperSelector.getIsInboundExternalFallbackIsForwarded,
      IciHelperSelector.getIsInboundExternalFallbackIsNoAnswer,
      IciHelperSelector.getIsInboundExternalFallbackBusy,
      IciHelperSelector.getIsInboundInternalIsRedirected,
      IciHelperSelector.getIsInboundInternalIsForwarded,
      IciHelperSelector.getIsInboundInternalIsNoAnswer,
      IciHelperSelector.getIsInboundInternalBusy,
      IciHelperSelector.getIsInboundExternalIsRedirected,
      IciHelperSelector.getIsInboundExternalIsForwarded,
      IciHelperSelector.getIsInboundExternalIsNoAnswer,
      IciHelperSelector.getIsInboundExternalBusy,
      IciHelperSelector.getIsInboundInternalFallback,
      IciHelperSelector.getIsInboundExternalFallback,
      IciHelperSelector.getIsInboundInternal,
      IciHelperSelector.getIsInboundExternal,
   ])
   public static getIciTypeById(
      getIsInboundInternalFallbackIsForwarded: (ici: number) => boolean,
      getIsInboundInternalFallbackIsNoAnswer: (ici: number) => boolean,
      getIsInboundInternalFallbackBusy: (ici: number) => boolean,
      getIsInboundExternalFallbackIsForwarded: (ici: number) => boolean,
      getIsInboundExternalFallbackIsNoAnswer: (ici: number) => boolean,
      getIsInboundExternalFallbackBusy: (ici: number) => boolean,
      getIsInboundInternalIsRedirected: (ici: number) => boolean,
      getIsInboundInternalIsForwarded: (ici: number) => boolean,
      getIsInboundInternalIsNoAnswer: (ici: number) => boolean,
      getIsInboundInternalBusy: (ici: number) => boolean,
      getIsInboundExternalIsRedirected: (ici: number) => boolean,
      getIsInboundExternalIsForwarded: (ici: number) => boolean,
      getIsInboundExternalIsNoAnswer: (ici: number) => boolean,
      getIsInboundExternalBusy: (ici: number) => boolean,
      getIsInboundInternalFallback: (ici: number) => boolean,
      getIsInboundExternalFallback: (ici: number) => boolean,
      getIsInboundInternal: (ici: number) => boolean,
      getIsInboundExternal: (ici: number) => boolean
   ) {
      return (ici: number) => {
         if (getIsInboundInternalFallbackIsForwarded(ici)) {
            return GreetingIciTypeEnum.INBOUND_INTERNAL_FALLBACK_FORWARDED;
         }
         if (getIsInboundInternalFallbackIsNoAnswer(ici)) {
            return GreetingIciTypeEnum.INBOUND_INTERNAL_FALLBACK_NO_ANSWER;
         }
         if (getIsInboundInternalFallbackBusy(ici)) {
            return GreetingIciTypeEnum.INBOUND_INTERNAL_FALLBACK_BUSY;
         }
         if (getIsInboundExternalFallbackIsForwarded(ici)) {
            return GreetingIciTypeEnum.INBOUND_EXTERNAL_FALLBACK_FORWARDED;
         }
         if (getIsInboundExternalFallbackIsNoAnswer(ici)) {
            return GreetingIciTypeEnum.INBOUND_EXTERNAL_FALLBACK_NO_ANSWER;
         }
         if (getIsInboundExternalFallbackBusy(ici)) {
            return GreetingIciTypeEnum.INBOUND_EXTERNAL_FALLBACK_BUSY;
         }
         if (getIsInboundInternalIsRedirected(ici)) {
            return GreetingIciTypeEnum.INBOUND_INTERNAL_REDIRECTED;
         }
         if (getIsInboundInternalIsForwarded(ici)) {
            return GreetingIciTypeEnum.INBOUND_INTERNAL_FORWARDED;
         }
         if (getIsInboundInternalIsNoAnswer(ici)) {
            return GreetingIciTypeEnum.INBOUND_INTERNAL_NO_ANSWER;
         }
         if (getIsInboundInternalBusy(ici)) {
            return GreetingIciTypeEnum.INBOUND_INTERNAL_BUSY;
         }
         if (getIsInboundExternalIsRedirected(ici)) {
            return GreetingIciTypeEnum.INBOUND_EXTERNAL_REDIRECTED;
         }
         if (getIsInboundExternalIsForwarded(ici)) {
            return GreetingIciTypeEnum.INBOUND_EXTERNAL_FORWARDED;
         }
         if (getIsInboundExternalIsNoAnswer(ici)) {
            return GreetingIciTypeEnum.INBOUND_EXTERNAL_NO_ANSWER;
         }
         if (getIsInboundExternalBusy(ici)) {
            return GreetingIciTypeEnum.INBOUND_EXTERNAL_BUSY;
         }
         if (getIsInboundInternalFallback(ici)) {
            return GreetingIciTypeEnum.INBOUND_INTERNAL_FALLBACK;
         }
         if (getIsInboundExternalFallback(ici)) {
            return GreetingIciTypeEnum.INBOUND_EXTERNAL_FALLBACK;
         }
         if (getIsInboundInternal(ici)) {
            return GreetingIciTypeEnum.INBOUND_INTERNAL;
         }
         if (getIsInboundExternal(ici)) {
            return GreetingIciTypeEnum.INBOUND_EXTERNAL;
         }
         return GreetingIciTypeEnum.ALL;
      };
   }

   @Selector([
      IciHelperSelector.getIsInboundInternalFallback,
      IciHelperSelector.getIsInboundExternalFallback,
      IciHelperSelector.getIsInboundInternal,
      IciHelperSelector.getIsInboundExternal,
   ])
   public static getIciGroupTypeById(
      getIsInboundInternalFallback: (ici: number) => boolean,
      getIsInboundExternalFallback: (ici: number) => boolean,
      getIsInboundInternal: (ici: number) => boolean,
      getIsInboundExternal: (ici: number) => boolean
   ) {
      return (ici: number) => {
         if (getIsInboundInternalFallback(ici)) {
            return GreetingIciTypeEnum.INBOUND_INTERNAL_FALLBACK;
         }
         if (getIsInboundExternalFallback(ici)) {
            return GreetingIciTypeEnum.INBOUND_EXTERNAL_FALLBACK;
         }
         if (getIsInboundInternal(ici)) {
            return GreetingIciTypeEnum.INBOUND_INTERNAL;
         }
         if (getIsInboundExternal(ici)) {
            return GreetingIciTypeEnum.INBOUND_EXTERNAL;
         }
         return GreetingIciTypeEnum.ALL;
      };
   }
}
